import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Eye, Flag, ShieldCheck } from "lucide-react";
import ReportListingDialog from "@/components/detail/ReportListingDialog";

/**
 * Footer utility row on the ad-detail page. Renders:
 *   • Share this listing (native Web Share → clipboard fallback)
 *   • Page views (from `listing.views`)
 *   • Community Watch: "Report this listing" link
 */
export default function ListingFooterRow({ listing }) {
  const [reportOpen, setReportOpen] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: listing.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard.");
      }
    } catch {
      // user dismissed — no-op
    }
  };

  return (
    <footer
      data-testid="ad-footer-row"
      className="mt-8 pt-6 border-t border-subtle"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-[color:var(--brand-blue)]">
        <button
          type="button"
          onClick={share}
          data-testid="ad-share-btn"
          className="inline-flex items-center gap-1.5 hover:underline font-semibold"
        >
          <Share2 className="w-3.5 h-3.5" /> Share this listing
        </button>
        <span className="text-slate-300">·</span>
        <span
          className="inline-flex items-center gap-1.5 text-slate-600"
          data-testid="ad-page-views"
        >
          <Eye className="w-3.5 h-3.5" /> Page views: {listing.views ?? 0}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        <span>Community Watch:</span>
        <a
          href={`/report/${listing.id}`}
          onClick={(e) => { e.preventDefault(); setReportOpen(true); }}
          data-testid="ad-report-link"
          className="inline-flex items-center gap-1 font-semibold text-red-600 hover:underline"
        >
          <Flag className="w-3 h-3" /> Report this listing
        </a>
      </div>
      <ReportListingDialog
        listingId={listing.id}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />
    </footer>
  );
}
